import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, clearToken, getToken, imageUrl } from "../lib/api";
import { toast } from "sonner";
import {
    Plus,
    Loader2,
    LogOut,
    Pencil,
    Trash2,
    ArrowUpDown,
    Save,
    X,
    BookOpen,
    Image as ImageIcon,
    Search as SearchIcon,
} from "lucide-react";

function AddPageDialog({ open, onClose, onCreated, existingCategories }) {
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("");
    const [text, setText] = useState("");
    const [image, setImage] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const reset = () => {
        setTitle("");
        setCategory("");
        setText("");
        setImage(null);
    };

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (!image) {
            toast.error("Please choose an image for the page");
            return;
        }
        setSubmitting(true);
        try {
            const form = new FormData();
            form.append("title", title);
            form.append("category", category || "GENERAL");
            form.append("text_content", text);
            form.append("image", image);
            const { data } = await api.post("/admin/pages", form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Page added");
            reset();
            onCreated(data);
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to add page");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="add-page-dialog">
            <div className="absolute inset-0 bg-medical-900/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto scroll-thin">
                <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-teal-700">
                            Add page
                        </div>
                        <h2 className="font-display text-xl font-bold text-medical-900">
                            New table-of-contents entry
                        </h2>
                    </div>
                    <button
                        data-testid="add-page-close"
                        onClick={onClose}
                        className="p-2 rounded hover:bg-slate-100"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-4">
                    <div>
                        <label className="block text-xs font-semibold text-medical-800 uppercase tracking-wider mb-1.5">
                            Title
                        </label>
                        <input
                            data-testid="add-page-title-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                            placeholder="e.g. Acute Heart Failure Poster"
                            className="w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-600"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-medical-800 uppercase tracking-wider mb-1.5">
                            Category
                        </label>
                        <input
                            data-testid="add-page-category-input"
                            value={category}
                            onChange={(e) => setCategory(e.target.value.toUpperCase())}
                            required
                            list="categories-list"
                            placeholder="e.g. MEDICAL"
                            className="w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-600 uppercase font-mono text-sm"
                        />
                        <datalist id="categories-list">
                            {existingCategories.map((c) => (
                                <option key={c} value={c} />
                            ))}
                        </datalist>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-medical-800 uppercase tracking-wider mb-1.5">
                            Searchable text (optional)
                        </label>
                        <textarea
                            data-testid="add-page-text-input"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={4}
                            placeholder="Paste OCR or descriptive text so this page becomes searchable."
                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-600 text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold text-medical-800 uppercase tracking-wider mb-1.5">
                            Page image
                        </label>
                        <label
                            data-testid="add-page-image-dropzone"
                            className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-slate-300 rounded-md hover:border-teal-700 cursor-pointer text-slate-500 text-sm"
                        >
                            <ImageIcon className="h-5 w-5" />
                            {image ? image.name : "Click to choose an image (PNG, JPG)"}
                            <input
                                data-testid="add-page-image-input"
                                type="file"
                                accept="image/*"
                                onChange={(e) => setImage(e.target.files?.[0] || null)}
                                className="hidden"
                            />
                        </label>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 h-10 rounded-md border border-slate-300 text-sm hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="add-page-submit"
                            type="submit"
                            disabled={submitting}
                            className="px-4 h-10 rounded-md bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                            Add page
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function EditPageDialog({ page, onClose, onUpdated }) {
    const [title, setTitle] = useState(page.title);
    const [category, setCategory] = useState(page.category);
    const [text, setText] = useState(page.text_content || "");
    const [order, setOrder] = useState(page.order);
    const [pageNumber, setPageNumber] = useState(page.page_number);
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.put(`/admin/pages/${page.id}`, {
                title,
                category,
                text_content: text,
                order: Number(order),
                page_number: Number(pageNumber),
            });
            toast.success("Updated");
            onUpdated(data);
            onClose();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="edit-page-dialog">
            <div className="absolute inset-0 bg-medical-900/40 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto scroll-thin">
                <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-teal-700">
                            Edit page · p.{String(page.page_number).padStart(3, "0")}
                        </div>
                        <h2 className="font-display text-xl font-bold text-medical-900 truncate">
                            {page.title}
                        </h2>
                    </div>
                    <button onClick={onClose} className="p-2 rounded hover:bg-slate-100" data-testid="edit-page-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5">Title</label>
                        <input
                            data-testid="edit-page-title-input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                            className="w-full h-10 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-600"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5">Category</label>
                        <input
                            data-testid="edit-page-category-input"
                            value={category}
                            onChange={(e) => setCategory(e.target.value.toUpperCase())}
                            required
                            className="w-full h-10 px-3 border border-slate-300 rounded-md uppercase font-mono text-sm focus:outline-none focus:ring-2 focus:ring-teal-600"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" title="The printed 'p.014' reference shown next to the page">Page number</label>
                        <input
                            data-testid="edit-page-number-input"
                            type="number"
                            value={pageNumber}
                            onChange={(e) => setPageNumber(e.target.value)}
                            required
                            min={1}
                            className="w-full h-10 px-3 border border-slate-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-teal-600"
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" title="Controls the position of this page in the table of contents (smaller = higher up)">Order</label>
                        <input
                            data-testid="edit-page-order-input"
                            type="number"
                            value={order}
                            onChange={(e) => setOrder(e.target.value)}
                            required
                            className="w-full h-10 px-3 border border-slate-300 rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-teal-600"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5">
                            Searchable text
                        </label>
                        <textarea
                            data-testid="edit-page-text-input"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={6}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-600"
                        />
                    </div>
                    <div className="md:col-span-2 flex justify-end gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 h-10 rounded-md border border-slate-300 text-sm hover:bg-slate-50"
                        >
                            Cancel
                        </button>
                        <button
                            data-testid="edit-page-submit"
                            type="submit"
                            disabled={submitting}
                            className="px-4 h-10 rounded-md bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            Save
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function AdminDashboard() {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ total_pages: 0, categories: 0 });
    const [filter, setFilter] = useState("");
    const [showAdd, setShowAdd] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const navigate = useNavigate();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [{ data: list }, { data: s }] = await Promise.all([
                api.get("/pages"),
                api.get("/admin/stats"),
            ]);
            setPages(list);
            setStats(s);
        } catch (err) {
            if (err?.response?.status === 401) {
                clearToken();
                navigate("/admin/login");
            } else {
                toast.error("Failed to load pages");
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        if (!getToken()) {
            navigate("/admin/login");
            return;
        }
        load();
    }, [load, navigate]);

    const onCreated = () => load();
    const onUpdated = () => load();

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this page? This cannot be undone.")) return;
        setDeletingId(id);
        try {
            await api.delete(`/admin/pages/${id}`);
            toast.success("Page deleted");
            setPages((p) => p.filter((x) => x.id !== id));
            // refresh stats so subtitle stays accurate
            const { data: s } = await api.get("/admin/stats");
            setStats(s);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        } finally {
            setDeletingId(null);
        }
    };

    const logout = () => {
        clearToken();
        toast.success("Signed out");
        navigate("/admin/login");
    };

    const filtered = filter
        ? pages.filter(
              (p) =>
                  p.title.toLowerCase().includes(filter.toLowerCase()) ||
                  p.category.toLowerCase().includes(filter.toLowerCase()),
          )
        : pages;

    const existingCategories = [...new Set(pages.map((p) => p.category))].sort();

    return (
        <div className="min-h-screen bg-medical-50" data-testid="admin-dashboard">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
                    <Link to="/" className="flex items-center gap-2 text-medical-900">
                        <BookOpen className="h-5 w-5 text-teal-700" />
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-teal-700">
                                Admin
                            </div>
                            <div className="font-display text-base font-bold leading-tight">
                                ED Whiteboard
                            </div>
                        </div>
                    </Link>
                    <div className="flex-1" />
                    <button
                        data-testid="admin-logout-button"
                        onClick={logout}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 hover:text-teal-700"
                    >
                        <LogOut className="h-3.5 w-3.5" />
                        Sign out
                    </button>
                </div>
            </header>

            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                    <div>
                        <h1 className="font-display text-3xl sm:text-4xl font-black text-medical-900 tracking-tight">
                            Manage pages
                        </h1>
                        <p className="text-sm text-slate-500 mt-1">
                            {stats.total_pages} pages across {stats.categories} categories.
                        </p>
                    </div>
                    <button
                        data-testid="admin-add-page-button"
                        onClick={() => setShowAdd(true)}
                        className="inline-flex items-center gap-2 px-4 h-11 bg-teal-700 hover:bg-teal-800 text-white rounded-md font-medium transition-colors"
                    >
                        <Plus className="h-4 w-4" />
                        Add page
                    </button>
                </div>

                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                    <div className="p-4 border-b border-slate-200 flex items-center gap-2">
                        <SearchIcon className="h-4 w-4 text-slate-400" />
                        <input
                            data-testid="admin-filter-input"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter by title or category"
                            className="flex-1 h-9 px-2 bg-transparent text-sm focus:outline-none"
                        />
                        <span className="text-[10px] font-mono text-slate-400">
                            {filtered.length} / {pages.length}
                        </span>
                    </div>

                    {loading ? (
                        <div className="p-10 flex justify-center" data-testid="admin-loading">
                            <Loader2 className="h-5 w-5 animate-spin text-teal-700" />
                        </div>
                    ) : (
                        <table className="w-full text-sm" data-testid="admin-pages-table">
                            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                                <tr>
                                    <th className="text-left px-4 py-2 font-semibold">#</th>
                                    <th className="text-left px-4 py-2 font-semibold">Category</th>
                                    <th className="text-left px-4 py-2 font-semibold">Title</th>
                                    <th className="text-left px-4 py-2 font-semibold">
                                        <ArrowUpDown className="h-3 w-3 inline" /> Order
                                    </th>
                                    <th className="text-right px-4 py-2 font-semibold">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((p) => (
                                    <tr
                                        key={p.id}
                                        data-testid={`admin-row-${p.id}`}
                                        className="border-t border-slate-100 hover:bg-slate-50"
                                    >
                                        <td className="px-4 py-2 font-mono text-[11px] text-slate-400">
                                            {String(p.page_number).padStart(3, "0")}
                                        </td>
                                        <td className="px-4 py-2">
                                            <span className="text-[9px] font-bold uppercase tracking-[0.18em] text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded">
                                                {p.category}
                                            </span>
                                        </td>
                                        <td className="px-4 py-2 text-medical-900 max-w-md">
                                            <Link to={`/p/${p.id}`} className="hover:text-teal-700 truncate block">
                                                {p.title}
                                            </Link>
                                        </td>
                                        <td className="px-4 py-2 font-mono text-[11px] text-slate-500">
                                            {p.order}
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <div className="inline-flex gap-1">
                                                <button
                                                    data-testid={`admin-edit-${p.id}`}
                                                    onClick={async () => {
                                                        const { data } = await api.get(`/pages/${p.id}`);
                                                        setEditing(data);
                                                    }}
                                                    className="p-1.5 rounded hover:bg-slate-100 text-medical-800"
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    data-testid={`admin-delete-${p.id}`}
                                                    onClick={() => handleDelete(p.id)}
                                                    disabled={deletingId === p.id}
                                                    className="p-1.5 rounded hover:bg-red-50 text-red-600 disabled:opacity-50"
                                                >
                                                    {deletingId === p.id ? (
                                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                                    ) : (
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    )}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            <AddPageDialog
                open={showAdd}
                onClose={() => setShowAdd(false)}
                onCreated={onCreated}
                existingCategories={existingCategories}
            />
            {editing && (
                <EditPageDialog
                    page={editing}
                    onClose={() => setEditing(null)}
                    onUpdated={onUpdated}
                />
            )}
        </div>
    );
}
